import { useEffect } from "react";
import { ArrowLeft, ShieldCheck, Mail, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { trackEvent } from "@/lib/analytics";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL } from "@/lib/data";

const SECTIONS = [
  {
    title: "1. Responsable de la protection des renseignements personnels",
    content: [
      "Conformément à la Loi modernisant des dispositions législatives en matière de protection des renseignements personnels (Loi 25, Québec), Solubail — Soluco Financial Group désigne son responsable de la protection des renseignements personnels :",
      "Hervé G. — Courriel : herve.g@solucofinancialgroup.ca — Téléphone : (514) 651-3779. Le responsable veille au respect de la présente politique et répond à toute demande d'accès, de rectification ou de retrait de consentement.",
    ],
  },
  {
    title: "2. Renseignements personnels que nous collectons",
    content: [
      "Via notre formulaire de demande d'approbation : nom complet, nom de l'entreprise, numéro de téléphone, courriel professionnel, type d'équipement recherché, montant estimé et précisions fournies volontairement.",
      "Via les témoins (cookies), avec votre consentement : données de navigation et d'interaction avec le site (pages consultées, clics, utilisation de la calculatrice), recueillies par Google Analytics et le Pixel Meta.",
    ],
  },
  {
    title: "3. Finalités de la collecte",
    content: [
      "Traiter votre demande de financement et vous rappeler dans un délai de 24 à 48 heures ouvrables.",
      "Mesurer l'achalandage du site, évaluer l'efficacité de nos communications marketing et améliorer votre expérience.",
      "Nous ne vendons jamais vos renseignements personnels à des tiers.",
    ],
  },
  {
    title: "4. Témoins (cookies) et technologies de suivi",
    content: [
      "Nous utilisons Google Analytics (mesure d'audience) et le Pixel Meta (mesure publicitaire). Conformément à la Loi 25, ces témoins ne sont activés qu'après votre consentement explicite, exprimé par le bandeau affiché lors de votre première visite.",
      "Vous pouvez accepter ou refuser ces témoins, et modifier votre choix en tout temps via le lien « Préférences de témoins » au bas de chaque page. Votre choix est conservé pour une durée de 12 mois.",
    ],
  },
  {
    title: "5. Communication à des tiers",
    content: [
      "Vos renseignements peuvent être traités par des fournisseurs de services agissant pour notre compte : Google LLC (Analytics, États-Unis), Meta Platforms Inc. (Pixel, États-Unis) et notre fournisseur d'envoi de courriels transactionnels.",
      "Ces renseignements peuvent donc être hébergés ou communiqués à l'extérieur du Québec. Nous nous limitons aux renseignements nécessaires aux finalités décrites.",
    ],
  },
  {
    title: "6. Conservation des renseignements",
    content: [
      "Les demandes de financement sont conservées le temps nécessaire au traitement de votre dossier et au respect de nos obligations légales, puis détruites de façon sécuritaire. Les données analytiques sont conservées selon les paramètres par défaut de Google Analytics (14 mois) et de Meta.",
    ],
  },
  {
    title: "7. Mesures de sécurité",
    content: [
      "Nous appliquons des mesures de protection raisonnables : transmission chiffrée (HTTPS), accès restreint aux renseignements, et limitation stricte du personnel autorisé à consulter les demandes.",
    ],
  },
  {
    title: "8. Vos droits en vertu de la Loi 25",
    content: [
      "Vous avez le droit d'accéder à vos renseignements personnels, d'en demander la rectification ou la suppression, de retirer votre consentement en tout temps et d'être informé de toute utilisation de vos renseignements.",
      "Pour exercer ces droits, écrivez-nous à herve.g@solucofinancialgroup.ca ou appelez le (514) 651-3779. Nous répondrons à votre demande dans un délai de 30 jours.",
    ],
  },
  {
    title: "9. Droit de porter plainte",
    content: [
      "Si vous estimez que vos droits ne sont pas respectés, vous pouvez porter plainte auprès de la Commission d'accès à l'information du Québec (CAI) : 1 888 528-7741 — cai.gouv.qc.ca.",
    ],
  },
  {
    title: "10. Modifications de la présente politique",
    content: [
      "Nous pouvons mettre à jour cette politique pour refléter l'évolution de nos pratiques ou de la loi. La version en vigueur est celle publiée sur cette page, avec sa date de dernière mise à jour.",
    ],
  },
];

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent("page_view", {
      page_path: "/confidentialite",
      page_title: "Politique de confidentialité — Solubail",
    });
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
  }, []);

  return (
    <div data-testid="privacy-policy-page" className="bg-[#F7F9FC] text-slate-900 min-h-screen">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="glass border-b border-slate-900/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
          <a data-testid="privacy-logo-link" href="/" className="flex items-center">
            <img src="/images/logo.png" alt="Solubail" className="h-9 w-auto rounded-md bg-white px-2.5 py-1 ring-1 ring-slate-900/10 shadow-sm" />
          </a>
          <a
            data-testid="privacy-back-button"
            href="/"
            className="inline-flex items-center gap-2 rounded-full border border-slate-900/20 px-5 py-2 text-sm font-semibold text-slate-700 hover:border-sky-400 hover:text-sky-600 transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-600 font-semibold flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Conforme à la Loi 25 du Québec
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[0.95] tracking-tight text-slate-900 mt-5">
            Politique de confidentialité
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="text-sm text-slate-9000 mt-5">
            Dernière mise à jour : 3 septembre 2026 — Solubail — Soluco Financial Group, Montréal (Québec), Canada.
          </p>
        </Reveal>

        <div className="mt-14 space-y-2">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={0.03}>
              <section data-testid={`privacy-section-${i + 1}`} className="rounded-2xl border border-slate-900/10 bg-white p-7 sm:p-9 hover:border-sky-400/30 transition-colors duration-500">
                <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-900">
                  {s.title}
                </h2>
                <div className="mt-4 space-y-3">
                  {s.content.map((p) => (
                    <p key={p.slice(0, 30)} className="text-sm sm:text-base text-slate-500 leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.05}>
          <div className="mt-12 rounded-2xl border border-sky-400/30 bg-white spotlight-card p-7 sm:p-9">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-900">
              Une question sur vos renseignements?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <a
                data-testid="privacy-email-link"
                href={`mailto:${EMAIL}`}
                className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-3 text-sm font-bold text-[#0A1220] hover:bg-sky-300 transition-colors duration-300"
              >
                <Mail className="w-4 h-4" />
                {EMAIL}
              </a>
              <a
                data-testid="privacy-phone-link"
                href={PHONE_TEL}
                className="inline-flex items-center gap-2 rounded-full border border-slate-900/20 px-6 py-3 text-sm font-semibold text-slate-900 hover:border-sky-400 hover:text-sky-600 transition-colors duration-300"
              >
                <Phone className="w-4 h-4" />
                {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </Reveal>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
